// ---------- Fields (the leaf-level content) ----------

import { jsPDF } from 'jspdf';

export type FieldType
  = | 'text'
    | 'dropdown'
    | 'number'
    | 'date'
    | 'image'
    | 'boolean';

export interface ImageInfo {
  url: string;
  alt?: string;
  width: number;
  height: number;
}

export interface ColorbarInfo {
  colormap: string;
  minValue: number;
  maxValue: number;
  maskType?: string;
}

export type FieldValue = string | number | boolean | ColorbarInfo | ImageInfo | null;

export interface FieldOption {
  id: string;
  label: string;
}

// ---------- Relationships ----------
// Describes how a field's value can be populated automatically instead of
// (or in addition to) direct user input. A field with no `relationship` is
// always purely manual
//
// Two kinds:
//   - 'derived': computed from other field(s) already on the report
//                (e.g. heartRate.delta = difference(heartRate.pre, heartRate.post))
//   - 'sourced': pulled from somewhere outside the report's own values
//                (system clock/user, the active DICOM study, an analysis pipeline)

export type DerivedAction
  = | 'sum'
    | 'mean'
    | 'difference'
    | 'min'
    | 'max'
    | 'age';

export interface DerivedRelationship {
  kind: 'derived';
  /**
   * Keys of the Field(s) this value is computed from. Order matters for
   *  'difference' (parentKeys[0] - parentKeys[1]).
   */
  parentKeys: string[];
  action: DerivedAction;
  /**
   * Recompute automatically whenever a parent value changes.
   *  Default true. Set false to require an explicit trigger .
   */
  isAutomatic?: boolean;
}

export interface SystemSource {
  kind: 'system';
  /** Which piece of ambient runtime info to pull. */
  field: 'date' | 'time' | 'user';
}

export interface DicomSource {
  kind: 'dicom';
  /** Path/tag identifying where in the DICOM dataset to read */
  sourceKey: string;
  /** Key of another report field that scopes this lookup */
  parentKey?: string;
}

export interface AnalysisSource {
  kind: 'analysis';
  pipelineId: string;
  sourceKey: string;
  parentKey?: string;
}

export interface FunctionTableSource {
  kind: 'function-table';
  axis:
    | 'SaxStack'
    | 'LaxMonoplane2CV'
    | 'LaxMonoplane4CV'
    | 'LaxBiplane'
    | string;
  sourceKey: string;
  parentKey?: string;
}

export interface FlowTableSource {
  kind: 'flow-table';
  roi: 'FlowRoi1' | 'FlowRoi2' | string;
  sourceKey: string;
  parentKey?: string;
}

export type Source
  = | SystemSource
    | DicomSource
    | AnalysisSource
    | FunctionTableSource
    | FlowTableSource;

export interface SourcedRelationship {
  kind: 'sourced';
  source: Source;
  /**
   * Re-fetch automatically whenever the underlying source updates.
   *  Default true.
   */
  isAutomatic?: boolean;
}

export type Relationship = DerivedRelationship | SourcedRelationship;

export function isDerivedRelationship(
  r: Relationship,
): r is DerivedRelationship {
  return r.kind === 'derived';
}

export function isSourcedRelationship(
  r: Relationship,
): r is SourcedRelationship {
  return r.kind === 'sourced';
}

/**
 * Normalizes parent-key lookups across relationship shapes — useful for
 *  building a dependency graph (e.g. "recompute X whenever Y changes").
 */
export function getRelationshipParentKeys(
  relationship: Relationship,
): string[] {
  if (relationship.kind === 'derived') return relationship.parentKeys;
  const { source } = relationship;
  if (
    source.kind === 'dicom'
    || source.kind === 'analysis'
    || source.kind === 'function-table'
    || source.kind === 'flow-table'
  ) {
    return source.parentKey ? [source.parentKey] : [];
  }
  return [];
}

export interface Field {
  key: string; // unique across the whole report
  type: FieldType;
  label?: string;
  defaultValue?: FieldValue; // used when no value has been set yet
  placeholder?: string;
  unit?: string;
  options?: FieldOption[]; // dropdown
  width?: number; // layout hint, fraction of row
  isEditable?: boolean; // default true
  isRequired?: boolean;
  isDisabled?: boolean;
  variant?: string;
  relationship?: Relationship;
  canAddAdditional?: boolean;
  neighborKeys?: string[];
}

export interface FieldState extends Field {
  value: FieldValue;
  lastChanged?: string;
  changedBy?: string;
}

// the live state, separate from structure — this is what you save/load
export type ValuesMap = Record<string, FieldValue>;

// A row is fields meant to sit side by side, e.g. [Gender, Age]
export type FieldRow = Field[];

interface BaseSection {
  id: string;
  title?: string; // "PATIENT", "BIOMARKERS"
  variant?: string; // theme override key
}

export interface HeaderLogo {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface HeaderSection extends BaseSection {
  kind: 'header';
  heading: string;
  logoHeight?: number;
  logo?: HeaderLogo & { align?: 'left' | 'right' };
  addedLogo?: HeaderLogo;
}

export interface FieldGroupSection extends BaseSection {
  kind: 'fieldGroup'; // PATIENT / PROTOCOL / SCAN / READERS / SCANNER
  rows: FieldRow[];
}

export interface FreeTextSection extends BaseSection {
  kind: 'freeText'; // the disclaimer paragraph, no title
  text: string;
}

export interface CannedChoice {
  label: string;
  text: string;
}

export interface CannedTextGroup {
  id: string;
  title: string;
  isMultiSelect: boolean;
  choices: CannedChoice[];
}

export interface RichTextSection extends BaseSection {
  kind: 'richText'; // SUMMARY box
  field: Field; // type: 'text', editable, multiline
  actions?: { id: string; label: string; icon?: string }[]; // "Auto-fill"
  cannedTextGroups?: CannedTextGroup[];
}

export interface StatRowSection extends BaseSection {
  kind: 'statRow';
  unit?: string;
  stats: Field[]; // key: "heartRate.baseline", label: "Baseline (min)", type: 'number'
}

export interface TableSection extends BaseSection {
  kind: 'table';
  columns: string[];
  colWidths?: (string | number)[];
  rows: { id: string; label: string; cells: Field[] }[]; // cells.length === columns.length - 1
  footnote?: string;
  keepHeadersInline?: boolean;
}

export interface ImageCell {
  id: string;
  titleField: Field; // key: "oxy.cell1.title", type: 'text', defaultValue: "CONTAINER TITLE"
  imageField: Field; // key: "oxy.cell1.image", type: 'image', defaultValue: null
}

export interface ImageGridSection extends BaseSection {
  kind: 'imageGrid';
  columns: number;
  imageHeight: number; // image height in px
  cells: ImageCell[];
}

export type Section
  = | HeaderSection
    | FieldGroupSection
    | FreeTextSection
    | RichTextSection
    | StatRowSection
    | TableSection
    | ImageGridSection;

export interface Theme {
  colors: {
    text: string;
    muted: string;
    border: string;
    background: string;
    accent: string;
    tableHeaderBg: string;
  };
  fonts: {
    heading: { family: string; size: number; weight: number };
    sectionLabel: {
      family: string;
      size: number;
      weight: number;
      uppercase?: boolean;
      letterSpacing?: number;
    };
    body: { family: string; size: number; weight: number };
    label: { family: string; size: number; weight: number };
    value: { family: string; size: number; weight: number };
  };
  spacing: { sectionGap: number; fieldGap: number; padding: number };
  borderRadius: number;
  imagePlaceholder: {
    borderStyle: 'dashed' | 'solid';
    borderColor: string;
    background: string;
  };
  fieldVariants?: Record<
    string,
    Partial<{ background: string; borderRadius: number; padding: number }>
  >;
}

export function getValue(values: ValuesMap, field: Field): FieldValue {
  const v = values[field.key];
  return v !== undefined ? v : field.defaultValue ?? null;
}

export function setValue(
  values: ValuesMap,
  field: Field,
  next: FieldValue,
): ValuesMap {
  return { ...values, [field.key]: next };
}

// ---------- Top level ----------

export type ReportPanelType = 'sidebar' | 'main';

export interface Panel {
  panelId: ReportPanelType;
  width: number; // fraction of content width, 0–1
  background?: string;
  padding: { top: number; left: number; right: number; bottom: number };
  gap: number; // pt, between sections within this panel
  sections: Section[];
}

export const SizeToDims = {
  a4: { width: 794, height: 1123, unit: 'px' },
};

export type SupportedPageSize = keyof typeof SizeToDims;

export interface PageLayout {
  size: SupportedPageSize;
  margin: number; // pt, outer page margin
  columnGap: number; // pt, between sidebar and main
  panels: Panel[]; // currently only supports 2 panels (sidebar and main)
}

export interface ReportTemplate {
  id: string;
  label: string;
  theme: Theme;
  layout: PageLayout;
}

// ---------- Report Helpers ----------
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function computeColumns(
  doc: jsPDF,
  layout: PageLayout,
): { sidebar: Rect; main: Rect } {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - layout.margin * 2;
  const contentH = pageH - layout.margin * 2;

  const sidebarW = contentW * layout.panels[0].width;
  const mainW = contentW - sidebarW - layout.columnGap;

  return {
    sidebar: {
      x: layout.margin,
      y: layout.margin,
      width: sidebarW,
      height: contentH,
    },
    main: {
      x: layout.margin + sidebarW + layout.columnGap,
      y: layout.margin,
      width: mainW,
      height: contentH,
    },
  };
}

export type SectionRenderer<S extends Section = Section> = (
  doc: jsPDF,
  section: S,
  values: ValuesMap,
  theme: Theme,
  rect: Rect,
) => number;

export const renderTable: SectionRenderer<TableSection> = (
  doc,
  section,
  values,
  theme,
  rect,
) => {
  let localY = rect.y;
  if (section.title) {
    doc
      .setFont(theme.fonts.sectionLabel.family, 'bold')
      .setFontSize(theme.fonts.sectionLabel.size);
    doc.text(section.title, rect.x, localY);
    localY += theme.fonts.sectionLabel.size + 8;
  }
  const colWidth = rect.width / section.columns.length;
  doc
    .setFont(theme.fonts.label.family, 'bold')
    .setFontSize(theme.fonts.label.size);
  section.columns.forEach((col, i) =>
    doc.text(col, rect.x + i * colWidth, localY),
  );
  localY += theme.fonts.label.size + 6;

  doc.setFont(theme.fonts.value.family, 'normal');
  for (const row of section.rows) {
    doc.text(row.label, rect.x, localY);
    row.cells.forEach((cell, i) => {
      const display = getValue(values, cell) ?? '—';
      doc.text(String(display), rect.x + (i + 1) * colWidth, localY);
    });
    localY += theme.fonts.value.size + 8;
  }
  if (section.footnote) {
    const lines = doc.splitTextToSize(section.footnote, rect.width) as string[];
    doc.setFontSize(theme.fonts.body.size - 2).setTextColor(theme.colors.muted);
    doc.text(lines, rect.x, localY + 4);
    localY += lines.length * theme.fonts.body.size + 4;
  }
  return localY + theme.spacing.sectionGap;
};

export const renderImageGrid: SectionRenderer<ImageGridSection> = (
  doc,
  section,
  values,
  theme,
  rect,
) => {
  const { y } = rect;
  const gap = theme.spacing.fieldGap;
  const cellW = (rect.width - gap * (section.columns - 1)) / section.columns;
  const cellH = cellW * 0.65;

  section.cells.forEach((cell, i) => {
    const col = i % section.columns;
    const row = Math.floor(i / section.columns);
    const x = rect.x + col * (cellW + gap);
    const cy = y + row * (cellH + gap + 14);

    const title
      = getValue(values, cell.titleField) ?? cell.titleField.placeholder ?? '';
    doc.setFontSize(theme.fonts.label.size).text(String(title), x, cy);

    const imageUrl = getValue(values, cell.imageField);
    if (imageUrl) {
      doc.addImage(String(imageUrl), 'PNG', x, cy + 14, cellW, cellH);
    }
    else {
      doc.setDrawColor(theme.imagePlaceholder.borderColor);
      doc.setLineDashPattern([3, 3], 0);
      doc.roundedRect(x, cy + 14, cellW, cellH, 4, 4, 'S');
      doc.setLineDashPattern([], 0);
    }
  });
  const rows = Math.ceil(section.cells.length / section.columns);
  return y + rows * (cellH + gap + 14) + theme.spacing.sectionGap;
};

// ---------- Functions ----------

export function collectFields(input: Section[] | ReportTemplate): Field[] {
  const sections = Array.isArray(input) ? input : getAllSectionsAsArray(input);
  const out: Field[] = [];
  for (const s of sections) {
    switch (s.kind) {
      case 'fieldGroup':
        s.rows.forEach(row => out.push(...row));
        break;
      case 'richText':
        out.push(s.field);
        break;
      case 'statRow':
        out.push(...s.stats);
        break;
      case 'table':
        s.rows.forEach(r => out.push(...r.cells));
        break;
      case 'imageGrid':
        s.cells.forEach(c => out.push(c.titleField, c.imageField));
        break;
      case 'header':
      case 'freeText':
        break;
    }
  }
  return out;
}

// Functions

export function getAllSectionsAsArray(report: ReportTemplate): Section[] {
  const sections: Section[] = [];
  report.layout.panels.forEach((panel) => {
    panel.sections.forEach(section => sections.push(section));
  });
  return sections;
}

export function extractFields(sections: Section[]): Record<string, FieldState> {
  const fieldValues: Record<string, FieldState> = {};

  // Helper to register a field's key and its default value
  const processField = (field: Field) => {
    if (field && field.key) {
      // Fallback to null if no explicit defaultValue is defined
      fieldValues[field.key] = {
        ...field,
        value: field.defaultValue !== undefined ? field.defaultValue : null,
      };
    }
  };

  sections.forEach((section) => {
    switch (section.kind) {
      case 'fieldGroup':
        section.rows.forEach(row => row.forEach(processField));
        break;

      case 'richText':
        processField(section.field);
        break;

      case 'statRow':
        section.stats.forEach(processField);
        break;

      case 'table':
        section.rows.forEach(row => row.cells.forEach(processField));
        break;

      case 'imageGrid':
        section.cells.forEach((cell) => {
          processField(cell.titleField);
          processField(cell.imageField);
        });
        break;

      // Header and FreeText do not contain stateful form fields
      case 'header':
      case 'freeText':
      default:
        break;
    }
  });

  return fieldValues;
}
